/** Settings › Privacy cards added by M29: redaction, identity binding, egress, webhooks, retention, audit. Shell created by WP-M29-SCHEMA1: later work packages add keys inside their group (en and fil together, no dots inside key segments). */
export const privacyM29 = {
  redaction: {
    title: "Redact before AI",
    intro: "Personal data is masked before a message reaches the AI model: chat answers, tool choice, drafts, ticket intake, summaries and translations.",
    enforced: "Enforced",
    off: "Off",
    kindsLabel: "Kinds of personal data",
    kinds: {
      mobile: { label: "Mobile numbers", hint: "09171234567, +63 917 123 4567" },
      email: { label: "E-mail addresses", hint: "juan@example.ph" },
      national_id: { label: "National ID (PhilSys)", hint: "PhilID card and PhilSys numbers" },
      address: { label: "Addresses", hint: "Street, barangay, block and lot" },
      card_bank: { label: "Card and bank numbers", hint: "Cards (checked with Luhn), bank accounts, IBAN" },
      gov_id: { label: "Government ID numbers", hint: "TIN, SSS, UMID, PhilHealth, Pag-IBIG, passport, driver's licence" },
    },
    allOff: "Every kind is off here. An assistant that sets its own kinds under Guardrails still masks them; the others receive messages exactly as customers type them.",
    agentNote: "An assistant's own choice under Guardrails wins over this list; an assistant with no choice of its own follows it. New assistants start with every kind on.",
    previewTitle: "Preview: what the AI receives",
    previewInput: "Try a message",
    previewPlaceholder: "Type a message with a phone number, e-mail or ID number",
    previewLimit: "{{used}} of {{max}} characters",
    previewTooLong: "A message can be up to {{max}} characters. Shorten it to see what the AI receives.",
    previewSample: "Hindi pumasok yung 500 sa account 0012 3456 7890. Tawagan niyo ako sa 09171234567 / juan@x.ph",
    previewLoading: "Masking the message…",
    previewNothing: "Nothing in this message is masked with the kinds selected.",
    previewEmpty: "Type a message to see what the AI receives.",
    masked_one: "{{count}} value masked",
    masked_other: "{{count}} values masked",
    previewFailedTitle: "The preview did not load",
    previewFailedBody: "Your settings are safe. Try the preview again.",
    noAssistantTitle: "Create an assistant to preview",
    noAssistantBody: "The preview runs on the server with the same rules the AI gateway applies. Create your first assistant to try it.",
    noAssistantAction: "Create an assistant",
    save: "Save redaction",
    saved: "Redaction settings saved",
    readOnlyNote: "You can look at these settings but not change them.",
  },
  binding: {
    title: "Lookups use the verified customer",
    intro: "When the assistant looks up a customer's account, it asks about the customer who is signed in, never about someone named in the chat.",
    badge: "Bound to session",
    lockedTitle: "Identity fields are locked to the session",
    lockedBody: "The customer reference, tier and account status in a lookup come from the verified session. The AI model never sees them as inputs and a customer cannot change them by typing.",
    liveLabel: "Lookups in the last 30 days",
    count_one: "{{count}} lookup, all for the customer in the session",
    count_other: "{{count}} lookups, all for the customer in the session",
    countMore: "At least {{count}} lookups, all for the customer in the session",
    latest: "Latest lookup {{when}}",
    noneTitle: "No lookups yet",
    noneBody: "When the assistant looks up an account for a signed-in customer, each lookup is recorded here.",
    viewLog: "Open the lookup log",
    needsAudit: "The lookup log is open to members who can view the audit log.",
    failedTitle: "The lookup activity did not load",
    failedBody: "Nothing was changed. Try again in a moment.",
  },
  egress: {
    title: "Outbound IP addresses",
    intro: "Allow these on your firewall for connector calls. Calls also support mutual TLS, OAuth 2.0 client credentials and HMAC signing.",
    status: { healthy: "Healthy", degraded: "Degraded", unconfigured: "Not set up" },
    listLabel: "Outbound IP addresses",
    region: "Region {{region}}",
    copyAll: "Copy all",
    copyOne: "Copy {{address}}",
    copiedAll: "IP addresses copied",
    copiedOne: "{{address}} copied",
    verified: "Last verified {{when}}",
    neverVerified: "Not verified yet",
    degradedTitle: "The fixed-IP path is not answering",
    degradedBody: "Connectors marked \"requires fixed IP\" are paused until it recovers, so no call goes out from another address. Other connectors keep trying. We check again every few seconds.",
    webhooksOff: "Webhook deliveries do not leave from these addresses. They are signed, so check the Sagot-Signature header instead of the source address.",
    webhooksOn: "Webhook deliveries also leave from these addresses.",
    emptyTitle: "No fixed IP addresses yet",
    emptyBody: "Fixed outbound addresses are not turned on for this environment, so connector calls leave from changing addresses. Ask your Sagot contact to turn them on before you allowlist us.",
    recheck: "Check again",
    failedTitle: "The IP addresses did not load",
    failedBody: "Nothing was changed. Try again in a moment.",
  },
  webhooks: {
    title: "Webhooks carry the customer reference",
    intro: "Signed with Sagot-Signature. Events are thin: IDs and the reference your systems already use.",
    sampleLabel: "Webhook event sample",
    carries: "Conversation, message and ticket events include customer_ref. It is null for an anonymous visitor.",
    active_one: "{{count}} active webhook receives customer_ref",
    active_other: "{{count}} active webhooks receive customer_ref",
    noneActive: "No active webhook receives customer_ref yet. Turn on a webhook that listens to conversation, message or ticket events.",
    manage: "Manage webhooks",
    emptyTitle: "No webhooks yet",
    emptyBody: "Add a webhook to send signed events to your own systems. They carry customer_ref so you can match them to your customers.",
    emptyAction: "Add a webhook",
    needsView: "Members who can manage webhooks see here which endpoints receive the reference.",
    failedTitle: "The webhooks did not load",
    failedBody: "Nothing was changed. Try again in a moment.",
  },
  retention: {
    title: "Retention by data class",
    intro: "How long each kind of data is kept. A preset sets all of them at once; a legal hold stops the nightly purge for that class until you lift it.",
    presetLabel: "Preset",
    presets: {
      standard: { name: "Standard", body: "Chats 1 year, tickets 5 years, customer history 1 year, security records 2 years." },
      aml_5_years: { name: "AML 5 years", body: "Chats, tickets, customer history and security records for 5 years, for anti-money-laundering record keeping." },
    },
    custom: "Custom settings",
    customBody: "These settings were edited one by one.",
    classLabel: "Data class",
    keepLabel: "Kept for",
    holdLabel: "Legal hold",
    holdOn: "Legal hold on {{name}}",
    classes: {
      chats: { name: "Chats", hint: "Messages, citations and ratings of conversations" },
      tickets: { name: "Tickets", hint: "Closed tickets with their messages and files" },
      customer_context_history: { name: "Customer history", hint: "Tier and status changes your systems sent" },
      launch_sessions: { name: "Launch sessions", hint: "Secure launches, deleted 7 days after they end" },
      audit_security: { name: "Security and audit records", hint: "Launches, lookups, views and key changes" },
    },
    fixed: "Fixed",
    years_one: "{{count}} year",
    years_other: "{{count}} years",
    days_one: "{{count}} day",
    days_other: "{{count}} days",
    floorNote: "With this preset no assistant deletes chats sooner than {{days}} days, even when it has its own setting.",
    auditNote: "Security and audit records are append-only: they are kept at least this long and the nightly job never deletes them.",
    review: "Review changes",
    noChanges: "Nothing to apply: the settings already match.",
    dialog: {
      title: "Review retention changes",
      intro: "These are the changes this will make. Applying is recorded in the audit log.",
      colClass: "Data class",
      colChange: "Change",
      from: "from {{from}}",
      to: "to {{to}}",
      holdOn: "Legal hold on: nothing in this class is deleted",
      holdOff: "Legal hold lifted",
      shorter: "Shorter: data older than the new period is removed at the next nightly purge (04:00).",
      longer: "Longer: nothing is deleted sooner.",
      empty: "No changes",
      confirm: "Apply changes",
      failed: "The changes could not be applied",
    },
    applied: "Retention settings applied",
    needsManage: "You can look at these settings but not change them.",
    ticketsNeedDesk: "Tickets follow the Service Desk. Set it up before changing ticket retention.",
    failedTitle: "The retention settings did not load",
    failedBody: "Nothing was changed. Try again in a moment.",
  },
  audit: {
    title: "Security activity",
    intro: "Launches, lookups, views and key changes. Tamper-evident; export for regulators.",
    subtitle: "Every change made in this workspace. Tamper-evident and append-only.",
    trailLabel: "Show",
    trails: { all: "All activity", launch: "Launches", customer: "Customers", api_key: "API keys" },
    events: {
      launch_minted: "Launch minted",
      launch_redeemed: "Code redeemed",
      launch_refused: "Launch refused",
      launch_revoked: "Launch revoked",
      customer_context_updated: "Customer context updated",
      customer_lookup: "Lookup bound to the session",
      customer_viewed: "Customer record viewed",
      customer_reference_revealed: "Customer reference revealed",
      api_key_allowlist_changed: "API key allowlist changed",
      api_key_signing_changed: "API key signing changed",
      api_key_ip_refused: "API key refused: address not allowed",
      api_key_rotated: "API key rotated",
      retention_preset_applied: "Retention settings applied",
    },
    exportCsv: "Export CSV",
    exporting: "Preparing the CSV…",
    exported: "CSV downloaded",
    exportFailed: "The CSV could not be downloaded",
    emptyTitle: "No security activity yet",
    emptyBody: "Launches, customer lookups and views, and API key changes appear here as they happen.",
    viewAll: "Open the full audit log",
    needsView: "Members who can view the audit log see launches, lookups and views here.",
    failedTitle: "The security activity did not load",
    failedBody: "Nothing was changed. Try again in a moment.",
    colTime: "Time",
    colEvent: "Event",
  },
} as const;
